import {useGetCompanyInfo} from '@/features/company-info/queries/useGetCompanyInfo'
import {CompanyInfoForm} from '@/features/company-info/components/company-info-form/CompanyInfoForm'
import {CompanyInfoFormValidationSchema} from '@/features/company-info/types'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {useUpdateCompanyInfo} from '@/features/company-info/queries/useUpdateCompanyInfo'
import {toast} from '@/utilities/toast/toast'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {PageCardWrapper, PageContent, PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {Divider} from '@/components/ui/divider/Divider'
import {Helmet} from 'react-helmet'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {CompanyInfoFormLoader} from '@/features/company-info/components/company-info-form-loader/CompanyInfoFormLoader'

export const CompanyInfo = () => {
    const {t} = useTranslation()
    const {data: companyInfo, isLoading, isError} = useGetCompanyInfo()

    const {mutate: updateCompanyInfo, isPending: isUpdating} = useUpdateCompanyInfo({
        onSuccess: () => {
            toast.success(t('company_info:update_success'))
        },
        onError: () => {
            toast.error(t('company_info:update_error'))
        }
    })

    const handleSubmit = (data: CompanyInfoFormValidationSchema) => {
        updateCompanyInfo(data)
    }

    if (isLoading) {
        return (
            <PageCardWrapper>
                <Helmet title={t('company_info:title')} />
                <CompanyInfoFormLoader />
            </PageCardWrapper>
        )
    }

    if (isError) {
        return (
            <PageCardWrapper>
                <Helmet title={t('company_info:title')} />
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            </PageCardWrapper>
        )
    }

    return (
        <PageCardWrapper>
            <Helmet title={t('company_info:title')} />
            <PageHeading align="center" justify="space-between">
                <Flexbox direction="row" justify="space-between" align="center" fullWidth gap={2}>
                    <Text render="h1" size="textXl" weight="500">
                        {t('company_info:title')}
                    </Text>
                    <Flexbox direction="row" gap={2}>
                        <Button variant="primaryNeutral" type="submit" form="company-info-form" disabled={isUpdating}>
                            {isUpdating && <Spinner size={14} />}
                            {t('company_info:updated_save_cta')}
                        </Button>
                    </Flexbox>
                </Flexbox>
            </PageHeading>
            <Divider direction="horizontal" />
            <PageContent padding={0}>
                <ScrollArea style={{height: 'calc(100vh - 150px)'}} scrollbar={<Scrollbar />}>
                    <CompanyInfoForm initialData={companyInfo} onSubmit={handleSubmit} />
                </ScrollArea>
            </PageContent>
        </PageCardWrapper>
    )
}
